import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { formatAgo, formatDateTime } from '../lib/format'
import { useAuth } from '../lib/auth'
import { betroffeneVersionen } from '../lib/advisory'
import type {
  Advisory,
  AlertRule,
  AuditEntry,
  ChannelKind,
  Customer,
  MaintenanceWindow,
  NotificationChannel,
  OnCallData,
  Role,
  User,
  SchwachstellenStand,
} from '../lib/types'
import { StatusBadge } from '../components/Status'
import { ErrorBox, Loading } from '../components/Feedback'
import { BereitschaftTab } from './Bereitschaft'
import { DokuPlattformTab } from '../components/DokuAnbindung'

type Tab =
  | 'benutzer'
  | 'rollen'
  | 'alarmregeln'
  | 'kanaele'
  | 'bereitschaft'
  | 'wartung'
  | 'advisories'
  | 'doku'
  | 'audit'

export function EinstellungenPage() {
  const { can, me } = useAuth()
  const [tab, setTab] = useState<Tab>('benutzer')

  const tabs: [Tab, string, boolean][] = [
    ['benutzer', 'Benutzer', can('user.manage')],
    ['rollen', 'Rollen', can('user.manage')],
    ['alarmregeln', 'Alarmregeln', can('settings.manage')],
    ['kanaele', 'Benachrichtigung', can('settings.manage')],
    ['bereitschaft', 'Bereitschaft', can('settings.manage')],
    ['wartung', 'Wartungsfenster', can('settings.manage')],
    ['advisories', 'Firmware-Hinweise', can('settings.manage')],
    ['doku', 'Doku-Plattform', me?.global_permissions.includes('settings.manage') ?? false],
    ['audit', 'Audit-Log', can('audit.view')],
  ]
  const visible = tabs.filter(([, , allowed]) => allowed)
  const active = visible.some(([key]) => key === tab) ? tab : (visible[0]?.[0] ?? 'benutzer')

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Einstellungen</h1>
          <div className="page-sub">Benutzer, Rechte, Alarmierung und Nachweise</div>
        </div>
      </div>

      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {visible.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className="btn btn-sm"
            aria-pressed={active === key}
            style={
              active === key
                ? { background: 'var(--acc-soft)', color: 'var(--acc)', fontWeight: 600 }
                : undefined
            }
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <section className="card">
          <div className="empty">
            <strong>Keine Einstellungen verfügbar</strong>
            <span>Für diesen Bereich fehlen die Berechtigungen.</span>
          </div>
        </section>
      ) : null}

      {active === 'benutzer' ? <UsersTab /> : null}
      {active === 'rollen' ? <RolesTab /> : null}
      {active === 'alarmregeln' ? <AlertRulesTab /> : null}
      {active === 'kanaele' ? <ChannelsTab /> : null}
      {active === 'bereitschaft' ? <BereitschaftTab /> : null}
      {active === 'wartung' ? <MaintenanceTab /> : null}
      {active === 'advisories' ? <AdvisoriesTab /> : null}
      {active === 'doku' ? <DokuPlattformTab /> : null}
      {active === 'audit' ? <AuditTab /> : null}
    </>
  )
}

// ------------------------------------------------------------------ Benutzer

function UsersTab() {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  // Leer: ein Konto der Technik; sonst ein Konto des Kundenportals für diesen Kunden.
  const [portalKunde, setPortalKunde] = useState('')

  const users = useQuery({ queryKey: ['users'], queryFn: ({ signal }) => apiGet<User[]>('/users', signal) })
  const roles = useQuery({ queryKey: ['roles'], queryFn: ({ signal }) => apiGet<Role[]>('/roles', signal) })
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
  })

  const create = useMutation({
    mutationFn: () =>
      apiSend<User>('POST', '/users', {
        email: email.trim(),
        name: name.trim(),
        password,
        ...(portalKunde ? { portal_customer_id: portalKunde } : {}),
      }),
    onSuccess: () => {
      setEmail('')
      setName('')
      setPassword('')
      setPortalKunde('')
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const toggle = useMutation({
    mutationFn: (vars: { id: string; disable: boolean }) =>
      apiSend<void>('POST', `/users/${vars.id}/${vars.disable ? 'disable' : 'enable'}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  const assign = useMutation({
    mutationFn: (vars: { userId: string; roleId: string; scopeType: string; scopeId?: string }) =>
      apiSend<void>('POST', `/users/${vars.userId}/roles`, {
        role_id: vars.roleId,
        scope_type: vars.scopeType,
        ...(vars.scopeId ? { scope_id: vars.scopeId } : {}),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  const revoke = useMutation({
    mutationFn: (assignmentId: string) => apiSend<void>('DELETE', `/role-assignments/${assignmentId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  if (users.isError) return <ErrorBox error={users.error} what="Die Benutzer" />

  return (
    <>
      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Benutzer anlegen</h2>
        </div>
        <form
          className="card-body row"
          style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}
          onSubmit={(e) => {
            e.preventDefault()
            if (email.trim() && name.trim() && password.length >= 12) create.mutate()
          }}
        >
          <div className="field" style={{ minWidth: 180 }}>
            <label htmlFor="user-name">Name</label>
            <input id="user-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field" style={{ minWidth: 220 }}>
            <label htmlFor="user-email">E-Mail-Adresse</label>
            <input
              id="user-email"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field" style={{ minWidth: 220 }}>
            <label htmlFor="user-password">Kennwort (mindestens 12 Zeichen)</label>
            <input
              id="user-password"
              className="input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="field" style={{ minWidth: 220 }}>
            <label htmlFor="user-art">Art des Kontos</label>
            <select
              id="user-art"
              className="input"
              value={portalKunde}
              onChange={(e) => setPortalKunde(e.target.value)}
            >
              <option value="">Technik (Rollen danach zuweisen)</option>
              {(customers.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  Kundenportal: {c.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={create.isPending || password.length < 12 || !email.trim() || !name.trim()}
          >
            Benutzer anlegen
          </button>
          {portalKunde ? (
            <p className="muted" style={{ flexBasis: '100%', margin: 0, fontSize: 13 }}>
              Ein Konto des Kundenportals sieht nur diesen Kunden und nur lesend: Geräte, Alarme, Monatsbericht und
              NIS2-Nachweis. Kein Fernzugriff, keine Konfigurationen, keine Rollen. Die Zwei-Faktor-Anmeldung richtet
              die Person bei der ersten Anmeldung ein.
            </p>
          ) : null}
          {create.isError ? (
            <span style={{ color: 'var(--crit)' }}>{(create.error as Error).message}</span>
          ) : null}
        </form>
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Benutzer</h2>
        </div>
        {users.isPending ? (
          <Loading what="Benutzer" />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>MFA</th>
                <th>Letzte Anmeldung</th>
                <th>Rollen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(users.data ?? []).map((u) => (
                <tr key={u.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{u.name}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {u.email}
                    </div>
                  </td>
                  <td>
                    {u.disabled_at ? (
                      <StatusBadge state="crit" label="Gesperrt" />
                    ) : (
                      <StatusBadge state="ok" label="Aktiv" />
                    )}
                  </td>
                  <td>
                    {u.mfa_enabled ? (
                      <StatusBadge state="ok" label="Eingerichtet" />
                    ) : (
                      <StatusBadge state="warn" label="Fehlt" />
                    )}
                  </td>
                  <td className="muted">{formatAgo(u.last_login_at)}</td>
                  <td>
                    <div className="stack" style={{ gap: 4 }}>
                      {u.portal_customer_id ? (
                        <span className="badge badge-info">Kundenportal · {u.portal_customer_name}</span>
                      ) : null}
                      {(u.roles ?? []).map((g) => (
                        <div key={g.assignment_id} className="row" style={{ gap: 6 }}>
                          <span className="badge badge-info">
                            {g.role_name}
                            {g.scope_type === 'global' ? ' (global)' : ` · ${g.scope_name}`}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{ border: 'none', background: 'transparent' }}
                            aria-label={`Rolle ${g.role_name} entziehen`}
                            onClick={() => revoke.mutate(g.assignment_id)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {u.portal_customer_id ? null : (
                        <RoleAssigner
                          roles={roles.data ?? []}
                          customers={customers.data ?? []}
                          onAssign={(roleId, scopeType, scopeId) =>
                            assign.mutate({ userId: u.id, roleId, scopeType, scopeId })
                          }
                        />
                      )}
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => toggle.mutate({ id: u.id, disable: !u.disabled_at })}
                    >
                      {u.disabled_at ? 'Entsperren' : 'Sperren'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {toggle.isError ? (
          <div className="card-body" style={{ color: 'var(--crit)' }}>
            {(toggle.error as Error).message}
          </div>
        ) : null}
      </section>
    </>
  )
}

function RoleAssigner({
  roles,
  customers,
  onAssign,
}: {
  roles: Role[]
  customers: Customer[]
  onAssign: (roleId: string, scopeType: string, scopeId?: string) => void
}) {
  const [roleId, setRoleId] = useState('')
  const [scope, setScope] = useState('global')

  return (
    <div className="row" style={{ gap: 6 }}>
      <select
        className="input"
        style={{ height: 28, fontSize: 12, width: 140 }}
        aria-label="Rolle"
        value={roleId}
        onChange={(e) => setRoleId(e.target.value)}
      >
        <option value="">Rolle …</option>
        {roles.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
      </select>
      <select
        className="input"
        style={{ height: 28, fontSize: 12, width: 160 }}
        aria-label="Geltungsbereich"
        value={scope}
        onChange={(e) => setScope(e.target.value)}
      >
        <option value="global">Alle Kunden</option>
        {customers.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn btn-sm"
        disabled={!roleId}
        onClick={() => {
          onAssign(roleId, scope === 'global' ? 'global' : 'customer', scope === 'global' ? undefined : scope)
          setRoleId('')
        }}
      >
        Zuweisen
      </button>
    </div>
  )
}

// -------------------------------------------------------------------- Rollen

function RolesTab() {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [selected, setSelected] = useState<string[]>(['view'])

  const roles = useQuery({ queryKey: ['roles'], queryFn: ({ signal }) => apiGet<Role[]>('/roles', signal) })
  const permissions = useQuery({
    queryKey: ['permissions'],
    queryFn: ({ signal }) => apiGet<{ key: string; label: string }[]>('/permissions', signal),
  })

  const create = useMutation({
    mutationFn: () => apiSend<Role>('POST', '/roles', { name: name.trim(), permissions: selected }),
    onSuccess: () => {
      setName('')
      void queryClient.invalidateQueries({ queryKey: ['roles'] })
    },
  })
  const remove = useMutation({
    mutationFn: (id: string) => apiSend<void>('DELETE', `/roles/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['roles'] }),
  })

  return (
    <>
      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Rolle anlegen</h2>
        </div>
        <form
          className="card-body stack"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim()) create.mutate()
          }}
        >
          <div className="field" style={{ maxWidth: 320 }}>
            <label htmlFor="role-name">Name</label>
            <input id="role-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <fieldset style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 12 }}>
            <legend style={{ fontSize: 13, color: 'var(--muted)', padding: '0 6px' }}>Rechte</legend>
            <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
              {(permissions.data ?? []).map((p) => (
                <label key={p.key} className="row" style={{ gap: 6, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={selected.includes(p.key)}
                    onChange={(e) =>
                      setSelected((prev) =>
                        e.target.checked ? [...prev, p.key] : prev.filter((k) => k !== p.key),
                      )
                    }
                  />
                  {p.label}
                </label>
              ))}
            </div>
          </fieldset>
          <button type="submit" className="btn btn-primary" disabled={create.isPending || !name.trim()}>
            Rolle anlegen
          </button>
          {create.isError ? (
            <span style={{ color: 'var(--crit)' }}>{(create.error as Error).message}</span>
          ) : null}
        </form>
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Rollen</h2>
        </div>
        <table className="data">
          <thead>
            <tr>
              <th>Rolle</th>
              <th>Rechte</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(roles.data ?? []).map((r) => (
              <tr key={r.id}>
                <td>
                  <div style={{ fontWeight: 600 }}>{r.name}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {r.built_in ? 'mitgeliefert' : 'eigene Rolle'}
                  </div>
                </td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {r.permissions.join(', ')}
                </td>
                <td>
                  {r.built_in ? null : (
                    <button type="button" className="btn btn-sm" onClick={() => remove.mutate(r.id)}>
                      Löschen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  )
}

// -------------------------------------------------------------- Alarmregeln

function AlertRulesTab() {
  const queryClient = useQueryClient()
  const rules = useQuery({
    queryKey: ['alert-rules'],
    queryFn: ({ signal }) => apiGet<AlertRule[]>('/alert-rules', signal),
  })
  const remove = useMutation({
    mutationFn: (id: string) => apiSend<void>('DELETE', `/alert-rules/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alert-rules'] }),
  })

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Alarmregeln</h2>
      </div>
      {rules.isPending ? (
        <Loading what="Regeln" />
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Regel</th>
              <th>Messgröße</th>
              <th>Warnung</th>
              <th>Kritisch</th>
              <th>Dauer</th>
              <th>Geltung</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(rules.data ?? []).map((r) => (
              <tr key={r.id}>
                <td style={{ fontWeight: 600 }}>{r.name}</td>
                <td className="mono">{r.metric}</td>
                <td className="mono">{r.warn_value ?? '–'}</td>
                <td className="mono">{r.crit_value ?? '–'}</td>
                <td className="mono">{r.for_seconds} s</td>
                <td className="muted">{r.scope_type === 'global' ? 'alle' : r.scope_type}</td>
                <td>
                  <button type="button" className="btn btn-sm" onClick={() => remove.mutate(r.id)}>
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

// ------------------------------------------------------------------- Kanäle

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

/** Kanalarten, wie die Auswahl sie nennt. */
export const KANALARTEN: Record<ChannelKind, string> = {
  email: 'E-Mail',
  teams: 'Microsoft Teams',
  webhook: 'Webhook',
  ntfy: 'Push (ntfy)',
  sms: 'SMS (seven.io)',
  anruf: 'Anruf (seven.io)',
}

function ChannelsTab() {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [kind, setKind] = useState<ChannelKind>('email')
  const [target, setTarget] = useState('')
  // Nur für ntfy, SMS und Anruf.
  const [secret, setSecret] = useState('')
  const [server, setServer] = useState('')
  const [absender, setAbsender] = useState('')
  const [plan, setPlan] = useState('')
  const [nurKritisch, setNurKritisch] = useState(false)
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5])
  const [from, setFrom] = useState('08:00')
  const [to, setTo] = useState('17:00')
  const [alwaysCritical, setAlwaysCritical] = useState(true)

  const data = useQuery({
    queryKey: ['channels'],
    queryFn: ({ signal }) =>
      apiGet<{ channels: NotificationChannel[]; outbox: { pending: number; failed: number } }>(
        '/channels',
        signal,
      ),
  })

  const plaene = useQuery({
    queryKey: ['bereitschaft'],
    queryFn: ({ signal }) => apiGet<OnCallData>('/bereitschaft', signal),
  })
  const planName = (id: string) => plaene.data?.plaene.find((p) => p.id === id)?.name ?? 'unbekannter Plan'
  const anPlan = plan !== '' && (kind === 'email' || kind === 'sms' || kind === 'anruf')
  const zielFehlt = !anPlan && !target.trim()
  const schluesselFehlt = (kind === 'sms' || kind === 'anruf') && !secret.trim()

  const create = useMutation({
    mutationFn: () =>
      apiSend<NotificationChannel>('POST', '/channels', {
        name: name.trim(),
        kind,
        config: kanalConfig(),
        service_days: days,
        service_from: from,
        service_to: to,
        always_critical: alwaysCritical,
      }),
    onSuccess: () => {
      setName('')
      setTarget('')
      setSecret('')
      void queryClient.invalidateQueries({ queryKey: ['channels'] })
    },
  })

  function kanalConfig(): Record<string, string> {
    const extra: Record<string, string> = nurKritisch ? { nur_kritisch: 'true' } : {}
    const ziel: Record<string, string> = anPlan ? { bereitschaft: plan } : { to: target.trim() }
    switch (kind) {
      case 'email':
        return { ...ziel, ...extra }
      case 'teams':
        return { webhook_url: target.trim(), ...extra }
      case 'webhook':
        return { url: target.trim(), ...extra }
      case 'ntfy':
        return {
          topic: target.trim(),
          ...(server.trim() ? { server: server.trim() } : {}),
          ...(secret.trim() ? { token: secret.trim() } : {}),
          ...extra,
        }
      case 'sms':
      case 'anruf':
        return { ...ziel, api_key: secret.trim(), ...(absender.trim() ? { from: absender.trim() } : {}), ...extra }
    }
  }

  const test = useMutation({
    mutationFn: (id: string) => apiSend<void>('POST', `/channels/${id}/test`),
  })
  const remove = useMutation({
    mutationFn: (id: string) => apiSend<void>('DELETE', `/channels/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channels'] }),
  })

  const targetLabels: Record<ChannelKind, string> = {
    email: 'Empfängeradressen (durch Komma getrennt)',
    teams: 'Teams-Webhook-Adresse (https)',
    webhook: 'Webhook-Adresse (https)',
    ntfy: 'ntfy-Thema (wirkt wie ein Kennwort)',
    sms: 'Mobilnummern (+43…, durch Komma getrennt)',
    anruf: 'Mobilnummern (+43…, durch Komma getrennt)',
  }

  return (
    <>
      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Benachrichtigungskanal anlegen</h2>
          {data.data ? (
            <span className="muted" style={{ fontSize: 12 }}>
              Ausgang: {data.data.outbox.pending} offen, {data.data.outbox.failed} gescheitert
            </span>
          ) : null}
        </div>
        <form
          className="card-body stack"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim() && !zielFehlt && !schluesselFehlt) create.mutate()
          }}
        >
          <div className="row" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="field" style={{ minWidth: 180 }}>
              <label htmlFor="channel-name">Bezeichnung</label>
              <input id="channel-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="field" style={{ minWidth: 140 }}>
              <label htmlFor="channel-kind">Art</label>
              <select
                id="channel-kind"
                className="input"
                value={kind}
                onChange={(e) => setKind(e.target.value as typeof kind)}
              >
                {(Object.keys(KANALARTEN) as ChannelKind[]).map((k) => (
                  <option key={k} value={k}>
                    {KANALARTEN[k]}
                  </option>
                ))}
              </select>
            </div>
            {kind === 'email' || kind === 'sms' || kind === 'anruf' ? (
              <div className="field" style={{ minWidth: 200 }}>
                <label htmlFor="channel-an">An</label>
                <select id="channel-an" className="input" value={plan} onChange={(e) => setPlan(e.target.value)}>
                  <option value="">feste Empfänger</option>
                  {(plaene.data?.plaene ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      Bereitschaft: {p.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {!anPlan ? (
              <div className="field" style={{ minWidth: 280, flexGrow: 1 }}>
                <label htmlFor="channel-target">{targetLabels[kind]}</label>
                <input
                  id="channel-target"
                  className="input"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                />
              </div>
            ) : null}
          </div>

          {kind === 'ntfy' ? (
            <div className="row" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="field" style={{ minWidth: 260 }}>
                <label htmlFor="channel-server">ntfy-Server (leer: https://ntfy.sh)</label>
                <input
                  id="channel-server"
                  className="input"
                  value={server}
                  placeholder="https://ntfy.example.at"
                  onChange={(e) => setServer(e.target.value)}
                />
              </div>
              <div className="field" style={{ minWidth: 260 }}>
                <label htmlFor="channel-secret">Zugangstoken (optional)</label>
                <input
                  id="channel-secret"
                  className="input mono"
                  type="password"
                  autoComplete="off"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                />
              </div>
            </div>
          ) : null}
          {kind === 'sms' || kind === 'anruf' ? (
            <div className="row" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="field" style={{ minWidth: 260 }}>
                <label htmlFor="channel-secret">API-Schlüssel von seven.io</label>
                <input
                  id="channel-secret"
                  className="input mono"
                  type="password"
                  autoComplete="off"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                />
              </div>
              <div className="field" style={{ width: 200 }}>
                <label htmlFor="channel-absender">Absender (optional)</label>
                <input
                  id="channel-absender"
                  className="input"
                  maxLength={16}
                  value={absender}
                  onChange={(e) => setAbsender(e.target.value)}
                />
              </div>
            </div>
          ) : null}
          {anPlan ? (
            <span className="muted" style={{ fontSize: 12 }}>
              Gesendet wird an die Person, die im Plan „{planName(plan)}“ gerade Bereitschaft hat –{' '}
              {kind === 'email' ? 'an ihre E-Mail-Adresse' : 'an die Mobilnummer aus ihrem Konto'}.
            </span>
          ) : null}
          <label className="row" style={{ gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={nurKritisch} onChange={(e) => setNurKritisch(e.target.checked)} />
            Nur kritische Alarme senden
          </label>

          <fieldset style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 12 }}>
            <legend style={{ fontSize: 13, color: 'var(--muted)', padding: '0 6px' }}>Servicezeiten</legend>
            <div className="row" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="row" style={{ gap: 8 }}>
                {WEEKDAYS.map((label, index) => {
                  const day = index + 1
                  return (
                    <label key={label} className="row" style={{ gap: 4, fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={days.includes(day)}
                        onChange={(e) =>
                          setDays((prev) =>
                            e.target.checked ? [...prev, day] : prev.filter((d) => d !== day),
                          )
                        }
                      />
                      {label}
                    </label>
                  )
                })}
              </div>
              <div className="field" style={{ width: 110 }}>
                <label htmlFor="channel-from">von</label>
                <input id="channel-from" className="input mono" type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div className="field" style={{ width: 110 }}>
                <label htmlFor="channel-to">bis</label>
                <input id="channel-to" className="input mono" type="time" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
              <label className="row" style={{ gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={alwaysCritical}
                  onChange={(e) => setAlwaysCritical(e.target.checked)}
                />
                Kritische Alarme auch außerhalb
              </label>
            </div>
          </fieldset>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={create.isPending || !name.trim() || zielFehlt || schluesselFehlt}
          >
            Kanal anlegen
          </button>
          {create.isError ? (
            <span style={{ color: 'var(--crit)' }}>{(create.error as Error).message}</span>
          ) : null}
        </form>
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Kanäle</h2>
        </div>
        {(data.data?.channels ?? []).length === 0 ? (
          <div className="empty">
            <span>Noch kein Kanal eingerichtet. Ohne Kanal gibt es keine Benachrichtigung.</span>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Kanal</th>
                <th>Art</th>
                <th>Servicezeit</th>
                <th>Kritisch immer</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data.data?.channels ?? []).map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600 }}>
                    {c.name}
                    {c.config.nur_kritisch === 'true' ? (
                      <span className="badge badge-crit" style={{ marginLeft: 8 }}>
                        nur kritisch
                      </span>
                    ) : null}
                  </td>
                  <td className="muted">
                    {KANALARTEN[c.kind] ?? c.kind}
                    {c.config.bereitschaft ? ` an Bereitschaft: ${planName(c.config.bereitschaft)}` : ''}
                  </td>
                  <td className="muted mono">
                    {c.service_days.map((d) => WEEKDAYS[d - 1]).join(' ')} {c.service_from}–{c.service_to}
                  </td>
                  <td>
                    {c.always_critical ? (
                      <StatusBadge state="ok" label="Ja" />
                    ) : (
                      <StatusBadge state="off" label="Nein" />
                    )}
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={test.isPending}
                        onClick={() => test.mutate(c.id)}
                      >
                        Test senden
                      </button>
                      <button type="button" className="btn btn-sm" onClick={() => remove.mutate(c.id)}>
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {test.isError ? (
          <div className="card-body" style={{ color: 'var(--crit)' }}>
            {(test.error as Error).message}
          </div>
        ) : null}
        {test.isSuccess ? (
          <div className="card-body" style={{ color: 'var(--ok)' }}>
            Testnachricht zugestellt.
          </div>
        ) : null}
      </section>
    </>
  )
}

// ------------------------------------------------------------ Wartungsfenster

function MaintenanceTab() {
  const queryClient = useQueryClient()
  const [scope, setScope] = useState('global')
  const [reason, setReason] = useState('')
  const [startsAt, setStartsAt] = useState(localNow())
  const [endsAt, setEndsAt] = useState(localNow(2))

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
  })
  const windows = useQuery({
    queryKey: ['maintenance'],
    queryFn: ({ signal }) => apiGet<MaintenanceWindow[]>('/maintenance', signal),
  })

  const create = useMutation({
    mutationFn: () =>
      apiSend<MaintenanceWindow>('POST', '/maintenance', {
        scope_type: scope === 'global' ? 'global' : 'customer',
        ...(scope === 'global' ? {} : { scope_id: scope }),
        reason: reason.trim(),
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
      }),
    onSuccess: () => {
      setReason('')
      void queryClient.invalidateQueries({ queryKey: ['maintenance'] })
    },
  })
  const remove = useMutation({
    mutationFn: (id: string) => apiSend<void>('DELETE', `/maintenance/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['maintenance'] }),
  })

  return (
    <>
      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Wartungsfenster planen</h2>
        </div>
        <form
          className="card-body row"
          style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}
          onSubmit={(e) => {
            e.preventDefault()
            create.mutate()
          }}
        >
          <div className="field" style={{ minWidth: 200 }}>
            <label htmlFor="wartung-geltung">Geltungsbereich</label>
            <select id="wartung-geltung" className="input" value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="global">Alle Kunden</option>
              {(customers.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ minWidth: 200 }}>
            <label htmlFor="wartung-start">Beginn</label>
            <input
              id="wartung-start"
              className="input"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </div>
          <div className="field" style={{ minWidth: 200 }}>
            <label htmlFor="wartung-ende">Ende</label>
            <input
              id="wartung-ende"
              className="input"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </div>
          <div className="field" style={{ minWidth: 220, flexGrow: 1 }}>
            <label htmlFor="wartung-grund">Grund</label>
            <input id="wartung-grund" className="input" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={create.isPending}>
            Fenster anlegen
          </button>
          {create.isError ? (
            <span style={{ color: 'var(--crit)' }}>{(create.error as Error).message}</span>
          ) : null}
        </form>
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Geplante Fenster</h2>
        </div>
        {(windows.data ?? []).length === 0 ? (
          <div className="empty">
            <span>Kein Wartungsfenster geplant.</span>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Geltung</th>
                <th>Von</th>
                <th>Bis</th>
                <th>Grund</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(windows.data ?? []).map((m) => (
                <tr key={m.id}>
                  <td>{m.scope_type === 'global' ? 'Alle Kunden' : m.scope_name || m.scope_type}</td>
                  <td className="mono">{formatDateTime(m.starts_at)}</td>
                  <td className="mono">{formatDateTime(m.ends_at)}</td>
                  <td className="muted">{m.reason || '–'}</td>
                  <td>
                    <button type="button" className="btn btn-sm" onClick={() => remove.mutate(m.id)}>
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  )
}

function localNow(plusHours = 0): string {
  const d = new Date(Date.now() + plusHours * 3600_000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ----------------------------------------------------------------- Audit-Log

// -------------------------------------------------------- Firmware-Hinweise

function AdvisoriesTab() {
  const queryClient = useQueryClient()
  const [vendor, setVendor] = useState('')
  const [titel, setTitel] = useState('')
  const [url, setUrl] = useState('')
  const [schwere, setSchwere] = useState<'info' | 'warn' | 'crit'>('warn')
  const [ab, setAb] = useState('')
  const [behoben, setBehoben] = useState('')

  const list = useQuery({
    queryKey: ['advisories'],
    queryFn: ({ signal }) => apiGet<Advisory[]>('/advisories', signal),
  })

  const create = useMutation({
    mutationFn: () =>
      apiSend<Advisory>('POST', '/advisories', {
        vendor,
        titel,
        url: url || undefined,
        schwere,
        betrifft_ab: ab || undefined,
        behoben_in: behoben || undefined,
      }),
    onSuccess: () => {
      setTitel('')
      setUrl('')
      setAb('')
      setBehoben('')
      void queryClient.invalidateQueries({ queryKey: ['advisories'] })
      void queryClient.invalidateQueries({ queryKey: ['device-advisories'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => apiSend<void>('DELETE', `/advisories/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['advisories'] })
      void queryClient.invalidateQueries({ queryKey: ['device-advisories'] })
    },
  })

  return (
    <>
      <SchwachstellenAbgleich />
      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Firmware-Hinweise</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            Automatisch aus der KEV-Liste der CISA oder von Hand eingetragen
          </span>
        </div>
        {list.isPending ? (
          <Loading what="Hinweise" />
        ) : (list.data?.length ?? 0) === 0 ? (
          <div className="empty">
            <span>
              Noch kein Hinweis. Aktiv ausgenutzte Schwachstellen kommen mit dem nächsten Abgleich; eigene Hinweise
              aus dem PSIRT eines Herstellers lassen sich unten eintragen.
            </span>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Hersteller</th>
                <th>Hinweis</th>
                <th>Betrifft</th>
                <th>Schwere</th>
                <th>Geräte</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((a) => (
                <tr key={a.id}>
                  <td>{a.vendor}</td>
                  <td>
                    {a.url ? (
                      <a href={a.url} target="_blank" rel="noreferrer noopener">
                        {a.titel}
                      </a>
                    ) : (
                      a.titel
                    )}
                    {a.cve ? (
                      <span className="muted mono" style={{ fontSize: 12, display: 'block' }}>
                        {a.cve} · {a.produkt}
                        {a.ausgenutzt ? ' · aktiv ausgenutzt (CISA)' : ''}
                      </span>
                    ) : null}
                  </td>
                  <td className="mono">{betroffeneVersionen(a)}</td>
                  <td>
                    <span
                      className={`badge ${a.schwere === 'crit' ? 'badge-crit' : a.schwere === 'warn' ? 'badge-warn' : 'badge-info'}`}
                    >
                      {a.schwere}
                    </span>
                  </td>
                  <td>{a.betroffene_geraete ?? 0}</td>
                  <td>
                    {a.quelle === 'kev' ? (
                      <span className="muted" style={{ fontSize: 12 }}>
                        automatisch
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(a.id)}
                      >
                        Löschen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Hinweis eintragen</h2>
        </div>
        <form
          className="card-body stack"
          onSubmit={(e) => {
            e.preventDefault()
            create.mutate()
          }}
        >
          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            <div className="field" style={{ width: 160 }}>
              <label htmlFor="adv-vendor">Hersteller</label>
              <input
                id="adv-vendor"
                className="input"
                placeholder="fortinet"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
              />
            </div>
            <div className="field grow" style={{ minWidth: 240 }}>
              <label htmlFor="adv-titel">Titel</label>
              <input
                id="adv-titel"
                className="input"
                value={titel}
                onChange={(e) => setTitel(e.target.value)}
              />
            </div>
            <div className="field" style={{ width: 120 }}>
              <label htmlFor="adv-ab">Betrifft ab</label>
              <input
                id="adv-ab"
                className="input"
                placeholder="7.2.0"
                value={ab}
                onChange={(e) => setAb(e.target.value)}
              />
            </div>
            <div className="field" style={{ width: 120 }}>
              <label htmlFor="adv-behoben">Behoben in</label>
              <input
                id="adv-behoben"
                className="input"
                placeholder="7.4.0"
                value={behoben}
                onChange={(e) => setBehoben(e.target.value)}
              />
            </div>
            <div className="field" style={{ width: 130 }}>
              <label htmlFor="adv-schwere">Schwere</label>
              <select
                id="adv-schwere"
                className="input"
                value={schwere}
                onChange={(e) => setSchwere(e.target.value as 'info' | 'warn' | 'crit')}
              >
                <option value="info">Hinweis</option>
                <option value="warn">Warnung</option>
                <option value="crit">Kritisch</option>
              </select>
            </div>
            <div className="field grow" style={{ minWidth: 220 }}>
              <label htmlFor="adv-url">Advisory-Adresse</label>
              <input
                id="adv-url"
                className="input"
                placeholder="https://www.fortiguard.com/psirt/..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
          </div>
          {create.isError ? <ErrorBox error={create.error} what="Der Hinweis" /> : null}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={create.isPending || vendor.trim() === '' || titel.trim() === ''}
            >
              Eintragen
            </button>
          </div>
        </form>
      </section>
    </>
  )
}

interface VerifyPage {
  checked: number
  next_seq: number
  done: boolean
  intact: boolean
  broken_at?: number
  message: string
}

function AuditTab() {
  const [customerId, setCustomerId] = useState('')
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
  })
  const entries = useQuery({
    queryKey: ['audit', customerId || null],
    queryFn: ({ signal }) =>
      apiGet<AuditEntry[]>(customerId ? `/audit?anzahl=200&kunde=${customerId}` : '/audit?anzahl=200', signal),
  })
  // Die Prüfung läuft seitenweise: der Server prüft je Aufruf höchstens
  // eine begrenzte Zahl von Einträgen und nennt, wo es weitergeht.
  const verify = useMutation({
    mutationFn: async () => {
      let from = 0
      let checked = 0
      for (;;) {
        const page = await apiSend<VerifyPage>('POST', `/audit/verify?ab=${from}`)
        checked += page.checked
        if (page.done) return { ...page, checked }
        from = page.next_seq
      }
    },
  })

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Audit-Log</h2>
        <div className="row" style={{ gap: 10 }}>
          <select
            className="input"
            aria-label="Kunde"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
          >
            <option value="">Alle Kunden</option>
            {(customers.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {verify.data ? (
            <span className="row" style={{ gap: 6, fontSize: 13 }}>
              <StatusBadge
                state={verify.data.intact ? 'ok' : 'crit'}
                label={verify.data.intact ? 'Kette lückenlos' : 'Kette gebrochen'}
              />
              <span className="muted">{verify.data.message}</span>
            </span>
          ) : null}
          <button type="button" className="btn btn-sm" disabled={verify.isPending} onClick={() => verify.mutate()}>
            Kette prüfen
          </button>
        </div>
      </div>
      {entries.isPending ? (
        <Loading what="Einträge" />
      ) : (entries.data ?? []).length === 0 ? (
        <div className="empty">
          <span>Noch keine Einträge.</span>
        </div>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Zeit</th>
              <th>Wer</th>
              <th>Aktion</th>
              <th>Objekt</th>
              <th>Von wo</th>
            </tr>
          </thead>
          <tbody>
            {(entries.data ?? []).map((e) => (
              <tr key={e.seq}>
                <td className="mono">{formatDateTime(e.time)}</td>
                <td>{e.user_email || 'System'}</td>
                <td className="mono">{e.action}</td>
                <td className="muted mono" style={{ fontSize: 12 }}>
                  {e.object_type}
                  {e.object_id ? ` ${e.object_id.slice(0, 8)}` : ''}
                </td>
                <td className="muted mono">{e.client_ip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

/** Stand des automatischen Abgleichs der aktiv ausgenutzten Schwachstellen (Stufe 5.1). */
function SchwachstellenAbgleich() {
  const queryClient = useQueryClient()
  const { me } = useAuth()
  const darf = me?.global_permissions.includes('settings.manage') ?? false
  const stand = useQuery({
    queryKey: ['schwachstellen'],
    queryFn: ({ signal }) => apiGet<SchwachstellenStand>('/schwachstellen', signal),
  })
  const abgleich = useMutation({
    mutationFn: () => apiSend<{ relevant: number; neu: number }>('POST', '/schwachstellen/abgleich'),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['schwachstellen'] })
      void queryClient.invalidateQueries({ queryKey: ['advisories'] })
      void queryClient.invalidateQueries({ queryKey: ['device-advisories'] })
    },
  })
  const d = stand.data
  if (!d) return null
  const s = d.stand
  return (
    <section className="card">
      <div className="card-body row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <strong>Aktiv ausgenutzte Schwachstellen</strong>
        <span className="muted" style={{ fontSize: 13, flex: 1, minWidth: 240 }}>
          {!d.aktiv
            ? 'Der Abgleich ist in dieser Zentrale abgeschaltet.'
            : s.zuletzt
              ? `Zuletzt ${formatDateTime(s.zuletzt)}${s.katalog ? `, Katalog ${s.katalog}` : ''}: ${s.hinweise} ${
                  s.hinweise === 1 ? 'Hinweis' : 'Hinweise'
                } zu Firmware im Bestand. Täglich aus der KEV-Liste der CISA und den CVE-Einträgen der Hersteller.`
              : 'Noch kein Abgleich gelaufen. Er läuft täglich, der erste kurz nach dem Start der Zentrale.'}
        </span>
        {d.aktiv && darf ? (
          <button type="button" className="btn btn-sm" disabled={abgleich.isPending} onClick={() => abgleich.mutate()}>
            Jetzt abgleichen
          </button>
        ) : null}
        {s.zuletzt && (!s.ok || s.meldung) ? (
          <span style={{ color: s.ok ? 'var(--warn)' : 'var(--crit)', fontSize: 13, flexBasis: '100%' }}>
            {s.ok ? 'Einzelne Einträge nicht geladen: ' : 'Fehlgeschlagen: '}
            {s.meldung}
          </span>
        ) : null}
        {abgleich.isError ? (
          <span style={{ color: 'var(--crit)', fontSize: 13, flexBasis: '100%' }}>{(abgleich.error as Error).message}</span>
        ) : null}
      </div>
    </section>
  )
}
